import React, { useEffect, useState } from "react";
import { Box } from "@mui/material";
import { useWindowSize } from "@uidotdev/usehooks";
import SinConexionKawaii from "../SinConexionKawaii";
import { useMapFullscreen } from "../../context/MapFullscreenContext";
// Template116: Mapa con explicación (+ opcional sección inferior y tooltip responsive). Page23 y Page12 guia-londres.
// page.texts[0]=header, texts[1]=explicación, page.ui, page.maps[0], page.images, page.canvasImages, page.sections?.[0]
const Template116 = ({
	backgroundColor,
	textColor,
	headerBackgroundColor,
	headerTextColor,
	fontFamily,
	page,
}) => {
	const [loaded, setLoaded] = useState(false);
	const [fullscreen, setFullscreen] = useState(false);
	const [isOnline, setIsOnline] = useState(true);
	const [stickerError, setStickerError] = useState(false);
	const size = useWindowSize();
	const isSmallScreen = size.width <= 600;
	const mapFullscreenContext = useMapFullscreen();
	const setMapFullscreen = mapFullscreenContext?.setMapFullscreen;

	const openFullscreen = () => {
		setFullscreen(true);
		if (typeof setMapFullscreen === "function") setMapFullscreen(true);
	};
	const closeFullscreen = () => {
		setFullscreen(false);
		if (typeof setMapFullscreen === "function") setMapFullscreen(false);
	};

	useEffect(() => {
		return () => {
			if (typeof setMapFullscreen === "function") setMapFullscreen(false);
		};
	}, [setMapFullscreen]);

	useEffect(() => {
		if (typeof navigator !== "undefined") {
			setIsOnline(navigator.onLine);
			setLoaded(navigator.onLine);
		}

		const handleOnline = () => {
			setIsOnline(true);
			setLoaded(true);
		};
		const handleOffline = () => {
			setIsOnline(false);
			setLoaded(true);
		};

		window.addEventListener("online", handleOnline);
		window.addEventListener("offline", handleOffline);

		return () => {
			window.removeEventListener("online", handleOnline);
			window.removeEventListener("offline", handleOffline);
		};
	}, []);

	const headerText = page.texts?.[0]?.content;
	const logoImage = page.canvasImages?.find(
		(img) => img.id === "logo",
	)?.imageUrl;
	const explanationText = page.texts?.[1]?.content;
	const ui = page?.ui ?? page?.data?.ui ?? page?.data?.data?.ui ?? {};

	const getImageSrc = (id) => {
		if (page.images && Array.isArray(page.images)) {
			const found = page.images.find((img) => img?.id === id);
			if (found) return found.src ?? found.url;
		}
		return undefined;
	};

	const getCanvasImageUrl = (id) => {
		if (page.canvasImages && Array.isArray(page.canvasImages)) {
			const found = page.canvasImages.find((img) => img?.id === id);
			if (found && found.imageUrl) return found.imageUrl;
		}
		return undefined;
	};

	const mapaOffline = getImageSrc("mapa-offline");
	const stickerUrl =
		getCanvasImageUrl("park") ||
		getCanvasImageUrl("park-sticker") ||
		getCanvasImageUrl("covent");
	const mapUrl = page.maps?.[0];
	const sections = Array.isArray(page?.sections)
		? page.sections
		: Array.isArray(page?.data?.sections)
			? page.data.sections
			: Array.isArray(page?.data?.data?.sections)
				? page.data.data.sections
				: [];
	const bottomSection = sections[0];

	const actualHeaderColor = headerBackgroundColor || "#de5624";
	const contentColor = textColor || "#444";

	const tooltipBody = isSmallScreen
		? (ui.tooltipWalk?.bodyShort ??
			ui.tooltipWalk?.body ??
			ui.tooltipAppleMarket?.body)
		: (ui.tooltipWalk?.body ??
			ui.tooltipWalk?.bodyLong ??
			ui.tooltipAppleMarket?.body);

	if (fullscreen && mapUrl) {
		return (
			<Box
				sx={{
					position: "fixed",
					top: 0,
					left: 0,
					right: 0,
					bottom: 0,
					backgroundColor: "#fff",
					zIndex: 9999,
					display: "flex",
					flexDirection: "column",
				}}
			>
				<Box
					sx={{
						padding: "10px",
						textAlign: "right",
						backgroundColor: actualHeaderColor,
					}}
				>
					<Box
						component="button"
						onClick={closeFullscreen}
						sx={{
							backgroundColor: "#fff",
							color: actualHeaderColor,
							border: "none",
							borderRadius: "8px",
							padding: "6px 12px",
							fontWeight: 600,
							cursor: "pointer",
							fontFamily: `${fontFamily || "'Poppins', sans-serif"}`,
						}}
					>
						{ui.backButton}
					</Box>
				</Box>
				<iframe
					title={ui.mapTitleFullscreen}
					src={mapUrl}
					width="100%"
					height="100%"
					style={{ border: 0, flex: 1 }}
					allowFullScreen
					loading="lazy"
				></iframe>
			</Box>
		);
	}

	return (
		<Box
			sx={{
				display: "flex",
				flexDirection: "column",
				minHeight: "100%",
				height: "auto",
				width: "100%",
				backgroundColor: backgroundColor || "#f7f7f7",
				color: contentColor,
				position: "relative",
				fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
				"& *:not([data-template-header])": {
					fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
					color: "inherit",
				},
			}}
		>
			{/* CABECERA */}
			{headerText && (
				<Box
					data-block-id="header"
					data-template-header
					sx={{
						backgroundColor: actualHeaderColor,
						color: headerTextColor || "#fff",
						textAlign: "center",
						padding: "15px 0",
						fontSize: "14px",
						fontWeight: 600,
						flexShrink: 0,
						zIndex: 2,
					}}
				>
					{headerText}
					{logoImage && (
						<Box
							component="img"
							src={logoImage}
							alt="logo"
							sx={{
								width: isSmallScreen ? 66 : 80,
								position: "absolute",
								right: 2,
								top: 18,
								zIndex: 999999,
							}}
						/>
					)}
				</Box>
			)}

			{/* MAPA */}
			<Box
				data-block-id="map"
				sx={{
					flex: 1,
					minHeight: 320,
					marginTop: "-70px",
					position: "relative",
				}}
			>
				<Box
					sx={{
						position: "absolute",
						top: 0,
						left: 0,
						right: 0,
						bottom: 0,
					}}
				>
					{!loaded && isOnline && (
						<Box
							sx={{
								//display: "flex",
								//alignItems: "center",
								//justifyContent: "center",
								//height: "100%",
								position: "absolute",
								top: 0,
								left: 0,
								right: 0,
								bottom: 0,
								//zIndex: 1,
							}}
						>
							<Box
								sx={{
									width: 20,
									height: 20,
									border: "4px solid #ccc",
									borderTop: `4px solid ${actualHeaderColor}`,
									borderRadius: "50%",
									animation: "spin 1s linear infinite",
									"@keyframes spin": {
										"0%": { transform: "rotate(0deg)" },
										"100%": { transform: "rotate(360deg)" },
									},
								}}
							/>
						</Box>
					)}

					{isOnline && (
						<Box
							component="button"
							onClick={openFullscreen}
							sx={{
								position: "absolute",
								top: 80,
								right: 10,
								backgroundColor: actualHeaderColor,
								color: "#fff!important",
								padding: "8px 10px",
								fontSize: "11px",
								borderRadius: "8px",
								boxShadow: "1px 1px 2px rgba(0,0,0,0.2)",

								border: "none",
								cursor: "pointer",
							}}
						>
							{ui.fullscreenButton}
						</Box>
					)}

					{isOnline && mapUrl ? (
						<iframe
							title={ui.mapTitleEmbed}
							src={mapUrl}
							width="100%"
							height={"100%"}
							allowFullScreen=""
							style={{ border: 0, display: "block" }}
							onLoad={() => setLoaded(true)}
						></iframe>
					) : mapaOffline ? (
						<img
							src={mapaOffline}
							alt=""
							style={{
								width: "100%",
								height: "100%",
								objectFit: "cover",
								position: "relative",
							}}
						/>
					) : !isOnline ? (
						<SinConexionKawaii height="100%" fontFamily={fontFamily} />
					) : null}
				</Box>

				{stickerUrl && !stickerError && (
					<img
						src={stickerUrl}
						alt=""
						onError={() => setStickerError(true)}
						style={{
							width: 110,
							position: "absolute",
							bottom: -10,
							left: -18,
						}}
					/>
				)}

				{tooltipBody && (
					<Box
						sx={{
							position: "absolute",
							bottom: 60,
							left: 50,
							backgroundColor: "#fff8db",
							border: "2px solid #f5c400",
							borderRadius: "16px 16px 16px 0px",
							padding: "10px 12px",
							fontSize: 11,
							color: contentColor,
							boxShadow: "2px 2px 4px rgba(0,0,0,0.15)",
							maxWidth: isSmallScreen ? 200 : 200,
						}}
					>
						{tooltipBody}
					</Box>
				)}
			</Box>

			{/* EXPLICACIÓN */}
			{explanationText && (
				<Box
					data-block-id="text-1"
					sx={{
						display: "flex",
						gap: "16px",
						position: "relative",
						marginBottom: "12px",
					}}
				>
					<Box
						sx={{
							padding: "12px 16px",
							fontSize: 13,
							color: "#333",
						}}
					>
						{explanationText}
					</Box>
				</Box>
			)}

			{/* SECCIÓN INFERIOR OPCIONAL (ej. Kensington Gardens) */}
			{bottomSection &&
				(bottomSection.imageId ||
					bottomSection.title ||
					bottomSection.body) && (
					<Box
						data-block-id="text-2"
						sx={{
							display: "flex",
							gap: "16px",
							position: "relative",
							marginBottom: "12px",
							padding: "12px",
							paddingTop: 0,
						}}
					>
						{getImageSrc(bottomSection.imageId) && (
							<img
								src={getImageSrc(bottomSection.imageId)}
								alt=""
								style={{
									width: "40%",
									borderRadius: 12,
									objectFit: "cover",
									height: 110,
								}}
							/>
						)}
						<Box sx={{ width: bottomSection.imageId ? "60%" : "100%" }}>
							{bottomSection.title && (
								<Box
									component="h3"
									sx={{
										fontSize: 14,
										fontWeight: 700,
										textTransform: "uppercase",
										margin: 0,
									}}
								>
									{bottomSection.title}
								</Box>
							)}
							{bottomSection.body && (
								<Box sx={{ fontSize: 13, color: contentColor, margin: 0 }}>
									{bottomSection.body}
								</Box>
							)}
						</Box>
					</Box>
				)}
		</Box>
	);
};

Template116.thumbnail = "/images/template-1.png";

export default Template116;
